import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { Godown } from "../lib/api";
import {
  fmtInt,
  isPass,
  useDHDistrict,
  useDHGodowns,
  useDHReports,
} from "./data";
import { GodownReport, grainLabel } from "../statehead/Reports";

export function Reports() {
  const [params, setParams] = useSearchParams();
  const districtQ = useDHDistrict();
  const godownsQ = useDHGodowns();
  const reportsQ = useDHReports();

  const district = districtQ.data?.district;
  const godowns = godownsQ.data?.godowns ?? [];
  const reports = reportsQ.data?.reports ?? [];

  const [openPlace, setOpenPlace] = useState<string | null>(null);

  const selectedGodownId = params.get("godown")
    ? Number(params.get("godown"))
    : null;
  const selectGodown = (id: number | null) => {
    const next = new URLSearchParams(params);
    if (id == null) next.delete("godown");
    else next.set("godown", String(id));
    setParams(next, { replace: false });
  };

  const reportsOf = (godownId: number) =>
    reports.filter((r) => r.godown_id === godownId);
  const places = useMemo(() => {
    const set = new Set<string>();
    godowns.forEach((g) => set.add((g.place ?? "").trim()));
    return [...set].sort((a, b) =>
      a === "" ? 1 : b === "" ? -1 : a.localeCompare(b),
    );
  }, [godowns]);

  const crumb = (
    <p className="crumb">
      {(district?.name ?? "DISTRICT").toUpperCase()} / REPORTS
    </p>
  );

  // ---- report view --------------------------------------------------
  if (selectedGodownId != null) {
    const godown = godowns.find((g) => g.id === selectedGodownId) ?? null;
    return (
      <>
        {crumb}
        <GodownReport
          godown={godown}
          district={district ?? null}
          reports={godown ? reportsOf(godown.id) : []}
          backLabel="Back to godowns"
          onBack={() => {
            if (godown) setOpenPlace((godown.place ?? "").trim());
            selectGodown(null);
          }}
        />
      </>
    );
  }

  // ---- finder -------------------------------------------------------
  let crumbTrail: JSX.Element;
  let count: string;
  let cards: JSX.Element;

  if (openPlace != null) {
    const gs = godowns.filter((g) => (g.place ?? "").trim() === openPlace);
    crumbTrail = (
      <div className="find-crumb">
        <button onClick={() => setOpenPlace(null)}>ALL PLACES</button> /{" "}
        {(openPlace || "PLACE NOT SET").toUpperCase()}
      </div>
    );
    count = `${gs.length} GODOWN${gs.length === 1 ? "" : "S"}`;
    cards = (
      <div className="grid-cards">
        {gs.map((g) => (
          <GodownCard
            key={g.id}
            godown={g}
            reportCount={reportsOf(g.id).length}
            passed={reportsOf(g.id).filter((r) => isPass(r.verdict)).length}
            onOpen={() => selectGodown(g.id)}
          />
        ))}
      </div>
    );
  } else {
    crumbTrail = <div className="find-crumb">ALL PLACES</div>;
    count = `${places.filter(Boolean).length} PLACES · ${godowns.length} GODOWNS`;
    cards =
      godowns.length === 0 ? (
        <p className="no-match">
          You have no godowns yet. Add one from the Godowns page — reports show
          up here once its QC agent starts grading.
        </p>
      ) : (
        <div className="grid-cards">
          {places.map((pl) => {
            const gs = godowns.filter((g) => (g.place ?? "").trim() === pl);
            const gids = new Set(gs.map((g) => g.id));
            const filed = reports.filter(
              (r) => r.godown_id != null && gids.has(r.godown_id),
            ).length;
            return (
              <button
                key={pl || "__none"}
                className="gcard"
                onClick={() => setOpenPlace(pl)}
              >
                <b>{pl || "Place not set"}</b>
                <em>{(district?.name ?? "").toUpperCase()} DISTRICT</em>
                <span className="figs">
                  {gs.length} godown{gs.length === 1 ? "" : "s"} ·{" "}
                  {fmtInt(filed)} report{filed === 1 ? "" : "s"}
                </span>
                <span className="go">Open godowns →</span>
              </button>
            );
          })}
        </div>
      );
  }

  return (
    <>
      {crumb}
      <div className="page-head">
        <div>
          <h1>Reports</h1>
          <p>Find a place or godown to see every grading report filed there.</p>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="find-bar">
          <Search
            godowns={godowns}
            district={district?.name ?? ""}
            onPickPlace={(place) => setOpenPlace(place)}
            onPickGodown={(id) => selectGodown(id)}
          />
          <span className="find-count">{count}</span>
        </div>
        {crumbTrail}
        {cards}
      </div>
    </>
  );
}

function GodownCard({
  godown,
  reportCount,
  passed,
  onOpen,
}: {
  godown: Godown;
  reportCount: number;
  passed: number;
  onOpen: () => void;
}) {
  const pct = reportCount ? Math.round((passed / reportCount) * 100) : null;
  const grain = grainLabel(godown);
  return (
    <button className="gcard" onClick={onOpen}>
      <b>{godown.name}</b>
      <em>
        {(godown.place ?? "").trim() || "PLACE NOT SET"}
        {grain ? ` · ${grain}` : ""}
      </em>
      <span className={"figs" + (reportCount ? "" : " warn")}>
        {reportCount
          ? `${fmtInt(reportCount)} reports · ${pct}% passed`
          : "No reports yet"}
      </span>
      <span className="go">Open reports →</span>
    </button>
  );
}

type Hit =
  | { kind: "p"; label: string; meta: string; place: string }
  | { kind: "w"; label: string; meta: string; godownId: number };

function Search({
  godowns,
  district,
  onPickPlace,
  onPickGodown,
}: {
  godowns: Godown[];
  district: string;
  onPickPlace: (place: string) => void;
  onPickGodown: (godownId: number) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const blurTimer = useRef<number | null>(null);

  const hits: Hit[] = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    const out: Hit[] = [];
    const seen = new Set<string>();
    for (const g of godowns) {
      const place = (g.place ?? "").trim();
      if (place && !seen.has(place.toLowerCase()) &&
          place.toLowerCase().includes(needle)) {
        seen.add(place.toLowerCase());
        out.push({
          kind: "p",
          label: place,
          meta: `${district.toUpperCase()} DISTRICT`,
          place,
        });
      }
    }
    for (const g of godowns) {
      if (
        g.name.toLowerCase().includes(needle) ||
        (g.place ?? "").toLowerCase().includes(needle)
      ) {
        out.push({
          kind: "w",
          label: g.name,
          meta: (g.place ?? "").trim().toUpperCase() || "PLACE NOT SET",
          godownId: g.id,
        });
      }
    }
    return out.slice(0, 12);
  }, [q, godowns, district]);

  const pick = (i: number) => {
    const h = hits[i];
    if (!h) return;
    setQ("");
    setOpen(false);
    if (h.kind === "p") onPickPlace(h.place);
    else onPickGodown(h.godownId);
  };

  const GROUP: Record<Hit["kind"], string> = { p: "PLACES", w: "GODOWNS" };
  const IC: Record<Hit["kind"], string> = { p: "PL", w: "GD" };
  let lastKind = "";

  return (
    <div className="combo">
      <input
        className="search big"
        type="text"
        role="combobox"
        autoComplete="off"
        aria-expanded={open}
        aria-autocomplete="list"
        placeholder="Search a place or godown"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setSel(0);
          setOpen(!!e.target.value.trim());
        }}
        onFocus={() => setOpen(!!q.trim())}
        onBlur={() => {
          blurTimer.current = window.setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setSel((s) => Math.min(s + 1, hits.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSel((s) => Math.max(s - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            pick(sel);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && (
        <div className="combo-list" role="listbox">
          {hits.length === 0 ? (
            <p className="combo-none">
              Nothing matches. Try a place or a godown name.
            </p>
          ) : (
            hits.map((h, i) => {
              const header =
                h.kind !== lastKind ? (
                  <p className="combo-group" key={`g-${h.kind}`}>
                    {GROUP[h.kind]}
                  </p>
                ) : null;
              lastKind = h.kind;
              return (
                <span key={`${h.kind}-${i}`}>
                  {header}
                  <button
                    className={"combo-item" + (i === sel ? " sel" : "")}
                    role="option"
                    aria-selected={i === sel}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (blurTimer.current) window.clearTimeout(blurTimer.current);
                      pick(i);
                    }}
                  >
                    <span className={"ic " + h.kind}>{IC[h.kind]}</span>
                    <span>
                      <b>{h.label}</b>
                      <span>{h.meta}</span>
                    </span>
                  </button>
                </span>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
